<template>
  <main class="benefits-page">
    <!-- Hero / Title bar -->
    <div class="container">
        <div class="page-heading">
            <h1 class="benefits-title">Donator Benefits</h1>
            <p class="benefits-subtitle">
            Compare every rank side-by-side. Click a rank to spotlight it.
            </p>
        </div>
        

        <div class="mb-3 success-message text-center">
            <p>If you wish to donate, you must do so in-game by typing ::donate or talking to the Wise Old Man. You cannot donate from the website for the time being!</p>
        </div>
    </div>

    <!-- Comparison Table -->
    <div class="container benefits-body">
      <div class="benefits-shell">
        <div class="benefits-shell__header">
          <div class="benefits-shell__header-left">
            <span class="benefits-shell__kicker">Comparison</span>
            <span class="benefits-shell__divider">•</span>
            <span class="benefits-shell__hint">
              Showing <b>{{ filteredRows.length }}</b> perks
            </span>
            <button
                v-if="activeRankKey"
                class="btn btn-sm btn-outline-light benefits-search__clear-highlight"
                type="button"
                @click="activeRankKey = null"
            >
                Clear Highlight
            </button>
          </div>

          <div class="benefits-actions">
            <div class="benefits-search">
              <i class="fas fa-search benefits-search__icon"></i>
              <input
                v-model="search"
                type="text"
                class="form-control benefits-search__input"
                placeholder="Search perks (e.g., bank, cooldown, boost)..."
              />
              <button
                v-if="search"
                class="btn btn-sm btn-outline-light benefits-search__clear"
                type="button"
                @click="search = ''"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div class="benefits-table-wrap">
          <table class="benefits-table">
            <thead>
              <tr>
                <!-- Sticky left header -->
                <th class="perk-col sticky-col sticky-head">
                  <span class="perk-col__title">Perk</span>
                </th>

                <th
                  v-for="rank in ranks"
                  :key="rank.key"
                  class="rank-col sticky-head"
                  :class="[rank.className, columnClass(rank.key)]"
                  @click="setActive(rank.key)"
                  role="button"
                  tabindex="0"
                >
                  <div class="rank-head">
                    <img :src="rank.icon" :alt="rank.label" class="rank-head__icon" />
                    <div class="rank-head__meta">
                      <div class="rank-head__label">{{ rank.label }}</div>
                      <div class="rank-head__amount">{{ rank.amount }}</div>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="row in filteredRows"
                :key="row.perk"
                class="perk-row"
              >
                <!-- Sticky left perk name -->
                <td class="perk-cell sticky-col">
                  {{ row.perk }}
                </td>

                <td
                  v-for="rank in ranks"
                  :key="rank.key"
                  class="value-cell"
                  :class="columnClass(rank.key)"
                >
                  <template v-if="isYes(row.values[rank.key])">
                    <span class="benefits-pill is-yes">
                      <i class="fas fa-check"></i>
                      Yes
                    </span>
                  </template>

                  <template v-else-if="isNo(row.values[rank.key])">
                    <span class="benefits-pill is-no">
                      <i class="fas fa-times"></i>
                      No
                    </span>
                  </template>

                  <template v-else>
                    <span class="value-text">
                      {{ row.values[rank.key] }}
                    </span>
                  </template>
                </td>
              </tr>

              <tr v-if="filteredRows.length === 0">
                <td class="empty-cell sticky-col">
                  No perks found for “{{ search }}”.
                </td>
                <td
                  v-for="rank in ranks"
                  :key="rank.key"
                  class="empty-cell"
                >
                  —
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="benefits-footer-note">
          Tip: On mobile, scroll sideways to compare ranks. Click a rank header to spotlight that column.
        </div>
      </div>
    </div>

    <!-- Donator Zone Media -->
    <div class="container donator-zone-media">
        <div class="donator-zone-media__header">
            <h3>Donator Zones</h3>
            <p>Exclusive areas unlocked through donator ranks. Enjoy enhanced skilling, convenience, and private features.</p>
        </div>

        <div class="donator-zone-media__grid">
            <div class="donator-zone-media__item">
            <img src="@/assets/img/donator-benefits/dz_regular.png" alt="Regular Donator Zone" />
            <h5>Regular Donator Zone</h5>
            <p>Features: Banks near all skilling activities, Skilling nodes don't deplete, 3.5x XP for Sacrificing Bones, Red Chin hunting, Furnace requires 1 less coal, New herb patch and tree patch, Fishing spots move x2 slower, Range provides 10% less burn rate, All shops and slayer masters</p>
            </div>

            <div class="donator-zone-media__item">
            <img src="@/assets/img/donator-benefits/dz_epic.png" alt="Epic Donator Zone" />
            <h5>Epic Donator Zone</h5>
            <p>Features: Epic/Divine Weekly Chest, 4 new barrage areas for Jellies, Dust Devils, Nechryaels, and Abyssal Demons, Instant teleport to the center of the Abyss, Divine pool of restoration, New AFK plots, Black Chins, Redwood Trees, Dark Crabs + Minnows, Herb patch, and Tree patch</p>
            </div>
        </div>
    </div>


  </main>
</template>

<script setup>
import { computed, ref } from "vue";

// Icons (your requested import format)
import regularIcon from "@/assets/img/donator-benefits/regular.png";
import superIcon from "@/assets/img/donator-benefits/super.png";
import extremeIcon from "@/assets/img/donator-benefits/extreme.png";
import epicIcon from "@/assets/img/donator-benefits/epic.png";
import legendaryIcon from "@/assets/img/donator-benefits/legendary.png";
import mythicIcon from "@/assets/img/donator-benefits/mythic.png";
import divineIcon from "@/assets/img/donator-benefits/divine.png";
import exiledIcon from "@/assets/img/donator-benefits/exiled.png";

/**
 * Ranks + perks sourced from your spreadsheet.
 * This is structured for easy future edits:
 * - Add a new rank: add to ranks[] and ensure each row has a value for it.
 * - Add a new perk row: add one object in rows[].
 */

const ranks = [
  { key: "regular", label: "Regular", amount: "$10", icon: regularIcon, className: "rank-regular" },
  { key: "super", label: "Super", amount: "$50", icon: superIcon, className: "rank-super" },
  { key: "extreme", label: "Extreme", amount: "$100", icon: extremeIcon, className: "rank-extreme" },
  { key: "epic", label: "Epic", amount: "$250", icon: epicIcon, className: "rank-epic" },
  { key: "legendary", label: "Legendary", amount: "$500", icon: legendaryIcon, className: "rank-legendary" },
  { key: "mythic", label: "Mythic", amount: "$750", icon: mythicIcon, className: "rank-mythic" },
  { key: "divine", label: "Divine", amount: "$1,000", icon: divineIcon, className: "rank-divine" },
  { key: "exiled", label: "Exiled", amount: "$2,500", icon: exiledIcon, className: "rank-exiled" },
];

// Search + active column highlight
const search = ref("");
const activeRankKey = ref(null);

function setActive(key) {
  activeRankKey.value = activeRankKey.value === key ? null : key;
}

function columnClass(key) {
  // If no active selection, show normal table.
  if (!activeRankKey.value) return "";
  // Highlight selected column, dim others a bit.
  return activeRankKey.value === key ? "is-highlight" : "is-dim";
}

function isYes(v) {
  return String(v).trim().toLowerCase() === "yes";
}
function isNo(v) {
  return String(v).trim().toLowerCase() === "no";
}

// Rows = perks. Each row has a values map by rank key.
const rows = [
  {
    perk: "Drop Rate Bonus",
    values: { regular: "1%", super: "2%", extreme: "4%", epic: "6%", legendary: "8%", mythic: "10%", divine: "12%", exiled: "15%" },
  },
  {
    perk: "Pest Control Bonus Points",
    values: { regular: "1", super: "1", extreme: "3", epic: "3", legendary: "4", mythic: "5", divine: "5", exiled: "5" },
  },
  {
    perk: "God Wars KC Requirement",
    values: { regular: "8", super: "6", extreme: "4", epic: "2", legendary: "0", mythic: "0", divine: "0", exiled: "0" },
  },
  {
    perk: "% chance to save warrior's guild token",
    values: { regular: "10%", super: "10%", extreme: "15%", epic: "15%", legendary: "20%", mythic: "25%", divine: "25%", exiled: "40%" },
  },
  {
    perk: "Farming Patches Auto-Composted",
    values: { regular: "No", super: "Normal", extreme: "Normal", epic: "Normal", legendary: "Super", mythic: "Super", divine: "Ultra", exiled: "Ultra" },
  },
  {
    perk: "Double Rewards From Crystal chest",
    values: { regular: "No", super: "10%", extreme: "15%", epic: "15%", legendary: "20%", mythic: "25%", divine: "25%", exiled: "33%" },
  },
  {
    perk: "Restore Box Cooldown",
    values: { regular: "7 min", super: "6 min", extreme: "5 min", epic: "4 min", legendary: "2 min", mythic: "1 min", divine: "None", exiled: "None" },
  },
  {
    perk: "Increased Marks of Grace Chance",
    values: { regular: "None", super: "10%", extreme: "10%", epic: "10%", legendary: "10%", mythic: "10%", divine: "10%", exiled: "20%" },
  },
  {
    perk: "Bonus Vote Points",
    values: { regular: "None", super: "None", extreme: "1.5x", epic: "1.5x", legendary: "1.5x", mythic: "1.5x", divine: "2x", exiled: "2.5x" },
  },
  {
    perk: "Price for Zahur's Services",
    values: { regular: "None", super: "125gp", extreme: "100gp", epic: "75gp", legendary: "50gp", mythic: "Free", divine: "Free", exiled: "Free" },
  },
  {
    perk: "Yell Cooldown",
    values: { regular: "2 min", super: "1 min", extreme: "45 sec", epic: "30 sec", legendary: "15 sec", mythic: "5 sec", divine: "None", exiled: "None" },
  },
  {
    perk: "Boosted Superior Slayer Spawn Rate",
    values: { regular: "None", super: "None", extreme: "None", epic: "10%", legendary: "10%", mythic: "10%", divine: "10%", exiled: "15%" },
  },
  {
    perk: "Extra Roll at Wintertodt",
    values: { regular: "None", super: "1", extreme: "1", epic: "1", legendary: "2", mythic: "2", divine: "2", exiled: "3" },
  },
  {
    perk: "Extra Utility Task Slot",
    values: { regular: "None", super: "1", extreme: "2", epic: "3", legendary: "3", mythic: "3", divine: "3", exiled: "3" },
  },
  {
    perk: "Utility Scroll Bonus Chance (chance to get an extra scroll)",
    values: { regular: "10%", super: "17%", extreme: "23%", epic: "30%", legendary: "37%", mythic: "43%", divine: "50%", exiled: "75%" },
  },
  {
    perk: "Birdhouse Timers",
    values: { regular: "45 min", super: "40 min", extreme: "35 min", epic: "30 min", legendary: "25 min", mythic: "20 min", divine: "20 min", exiled: "15 min" },
  },
  {
    perk: "Xeric's wisdom (Default 25%)",
    values: { regular: "27%", super: "30%", extreme: "33%", epic: "36%", legendary: "40%", mythic: "45%", divine: "60%", exiled: "70%" },
  },
  {
    perk: "Dwarf Multicannon Space (Default 30)",
    values: { regular: "40", super: "50", extreme: "60", epic: "70", legendary: "90", mythic: "90", divine: "100", exiled: "140" },
  },
  {
    perk: "Total Preset Slots (Default 4)",
    values: { regular: "8", super: "10", extreme: "12", epic: "14", legendary: "16", mythic: "18", divine: "20", exiled: "24" },
  },
  {
    perk: "Discount on Points Lost in Raid When Dying",
    values: { regular: "5%", super: "10%", extreme: "10%", epic: "15%", legendary: "30%", mythic: "40%", divine: "50%", exiled: "60%" },
  },
  {
    perk: "Larran's Key Drop Boost (on task in Wilderness)",
    values: { regular: "5%", super: "10%", extreme: "15%", epic: "20%", legendary: "25%", mythic: "30%", divine: "40%", exiled: "50%" },
  },
  {
    perk: "Coal Bag Capacity",
    values: { regular: "56", super: "100", extreme: "175", epic: "250", legendary: "500", mythic: "600", divine: "750", exiled: "850" },
  },
  {
    perk: "Extra Time in Tears of Guthix Minigame",
    values: { regular: "10 sec", super: "20 sec", extreme: "25 sec", epic: "30 sec", legendary: "45 sec", mythic: "55 sec", divine: "70 sec", exiled: "80 sec" },
  },
  {
    perk: "Slayer Extension Scroll Multiplier",
    values: { regular: "-", super: "1.50x", extreme: "1.75x", epic: "2.00x", legendary: "2.25x", mythic: "2.50x", divine: "2.75x", exiled: "3.00x" },
  },
  {
    perk: "Open Chest / Casket Amount",
    values: { regular: "Open 1", super: "Open 1", extreme: "Open 10", epic: "Open All", legendary: "Open All", mythic: "Open All", divine: "Open All", exiled: "Open All" },
  },
  {
    perk: "Auto Pickup Marks of Grace in Rooftop Courses",
    values: { regular: "Yes", super: "Yes", extreme: "Yes", epic: "Yes", legendary: "Yes", mythic: "Yes", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "Ability to use noted coal when smelting",
    values: { regular: "Yes", super: "Yes", extreme: "Yes", epic: "Yes", legendary: "Yes", mythic: "Yes", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "Receive noted bar drops from metal dragons",
    values: { regular: "No", super: "Yes", extreme: "Yes", epic: "Yes", legendary: "Yes", mythic: "Yes", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "No heat damage taken in Karuulm Slayer dungeon",
    values: { regular: "No", super: "No", extreme: "No", epic: "Yes", legendary: "Yes", mythic: "Yes", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "Free Wilderness Resource Area Access",
    values: { regular: "No", super: "No", extreme: "No", epic: "No", legendary: "Yes", mythic: "Yes", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "Receive noted bars at blast furnace",
    values: { regular: "No", super: "No", extreme: "No", epic: "No", legendary: "No", mythic: "Yes", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "Cooking NPC Cost Per Item",
    values: { regular: "550 coins", super: "500 coins", extreme: "450 coins", epic: "400 coins", legendary: "350 coins", mythic: "300 coins", divine: "250 coins", exiled: "200 coins" },
  },
  {
    perk: "DZ Access",
    values: { regular: "Yes", super: "Yes", extreme: "Yes", epic: "Yes", legendary: "Yes", mythic: "Yes", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "EDZ Access",
    values: { regular: "No", super: "No", extreme: "No", epic: "Yes", legendary: "Yes", mythic: "Yes", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "Open Epic Weekly Mystery Chest (Free Donator Chest)",
    values: { regular: "No", super: "No", extreme: "No", epic: "Yes", legendary: "Yes", mythic: "Yes", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "Open Divine Weekly Mystery Chest (Free Advanced Donator Chest)",
    values: { regular: "No", super: "No", extreme: "No", epic: "No", legendary: "No", mythic: "No", divine: "Yes", exiled: "Yes" },
  },
  {
    perk: "5x of all Raid Orbs from Divine Chest at ::edz",
    values: { regular: "No", super: "No", extreme: "No", epic: "No", legendary: "No", mythic: "No", divine: "Yes", exiled: "Yes" },
  },

];

const filteredRows = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return rows;

  return rows.filter((r) => {
    if (r.perk.toLowerCase().includes(term)) return true;
    // Search values too (helpful for things like "free", "none", "30%")
    return ranks.some((rk) => String(r.values[rk.key] ?? "").toLowerCase().includes(term));
  });
});
</script>

<style scoped>


/* Overall shell: flatter, darker, same borders as modal */
.benefits-shell {
  background-color: #161310;
  border: 1px solid #0f0c0a;
  border-radius: 0;
  box-shadow: none;
}

/* Header bar: match the modal container feel */
.benefits-shell__header {
  background-color: #0f0c0a;
  border-bottom: 1px solid #241f19;
  padding: 18px 20px;
}

/* Table behaves like the modal: grid rows, fixed column widths */
.benefits-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 0; /* modal relies on grid widths instead */
}

/* Each TR is a grid like your modal */
.benefits-table thead tr,
.benefits-table tbody tr {
  display: grid;
  grid-template-columns: 2.5fr repeat(8, 1fr);
}

/* Responsive grid columns (copied vibe from modal) */
@media (max-width: 1400px) {
  .benefits-table thead tr,
  .benefits-table tbody tr {
    grid-template-columns: auto repeat(8, 140px);
  }
}

@media (max-width: 1200px) {
  .benefits-table thead tr,
  .benefits-table tbody tr {
    grid-template-columns: 150px repeat(8, 100px);
  }
}

@media (max-width: 768px) {
  .benefits-table thead tr,
  .benefits-table tbody tr {
    grid-template-columns: 150px repeat(8, 100px);
  }
}

/* Cell padding like modal */
.benefits-table thead th,
.benefits-table tbody td {
  padding: 20px 15px;
}

/* Alternate row shading for readability */
.benefits-table tbody tr:nth-child(odd) td {
  background-color: #161310; /* base modal background */
}

.benefits-table tbody tr:nth-child(even) td {
  background-color: #14110e; /* slightly darker */
}

/* Hover state still wins */
.benefits-table tbody tr:hover td {
  background-color: #1b1713;
}

/* Keep column highlight visible on striped rows */
.benefits-table td.is-highlight {
  background-color: #0f0c0a !important;
}

/* Slight emphasis on perk name column */
.benefits-table tbody tr:nth-child(even) .perk-cell {
  background-color: #15120f;
}


/* Remove old sticky positioning (grid layout + modal style) */
.sticky-head,
.sticky-col {
  position: static !important;
  top: auto !important;
  left: auto !important;
  z-index: auto !important;
  background: transparent !important;
  border-right: none !important;
}

/* Row background/borders like modal */
.benefits-table thead th {
  background-color: #0f0c0a;
  border-bottom: 1px solid #241f19;
}

.perk-row td {
  background-color: #161310;
  border-bottom: 1px solid #241f19;
}

.perk-row:hover td {
  background-color: #14110e;
}

/* Perk column text matches modal feature color */
.perk-cell {
  color: #d8cab5;
  font-weight: 700;
}

/* Rank header aligns center like modal .rank */
.rank-col,
.value-cell {
  text-align: center;
}

.value-text {
  font-weight: 900;
  color: #f0d37a;
  text-shadow: 0 0 12px rgba(0,0,0,0.7);
}

/* Rank header becomes stacked on smaller screens (modal behavior) */
@media (max-width: 1400px) {
  .rank-head {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
}

/* Rank icon sizing like modal */
.rank-head__icon {
  width: 17px;
  height: auto;
  max-width: 17px;
  object-fit: contain;
}

/* Header label/amount */
.rank-head__label {
  font-weight: 800;
  line-height: 1.1;
}
.rank-head__amount {
  opacity: 0.85;
  font-size: 0.85rem;
}

/* Highlighting: keep your selection UX but make it modal-subtle */
.is-highlight {
  background-color: #0f0c0a !important;
  box-shadow: inset 0 0 0 1px #241f19;
}

.is-dim {
  opacity: 0.55;
  filter: saturate(0.85);
}

.benefits-pill.is-yes {
  border-color: rgba(80, 220, 140, 0.28);
  color: rgba(155, 255, 205, 0.50);
}
.benefits-pill.is-no {
  border-color: rgba(255, 90, 90, 0.28);
  color: rgba(255, 170, 170, 0.50);
}

/* Pills: slightly flatter to match modal */
.benefits-pill {
  background-color: #0f0c0a;
  border: 1px solid #241f19;
  border-radius: 999px;
  padding: 6px 10px;
  font-weight: 800;
}

/* Keep your rank colors, but remove glowing */
.rank-regular { color: #2ea3ff; }
.rank-super { color: #2df08b; }
.rank-extreme { color: #ff3b3b; }
.rank-epic { color: #ffffff; }
.rank-legendary { color: #b04dff; }
.rank-mythic { color: #c7c7c7; }
.rank-divine { color: #ff9a2a; }
.rank-exiled { color: #fff42a; }

/* Footer note consistent with modal containers */
.benefits-footer-note {
  border-top: 1px solid #241f19;
  background-color: #0f0c0a;
  padding: 18px 20px;
  opacity: 0.85;
}

/* Table wrapper: allow horizontal scroll like modal grid */
.benefits-table-wrap {
  overflow-x: auto;
  overflow-y: hidden;
  max-height: none;
}

.benefits-actions {
    padding-top: 16px;
    padding-bottom: 0px;
}

.benefits-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
}
.benefits-search__icon {
  position: absolute;
  left: 12px;
  top: 13px;
  opacity: 0.55;
}

.benefits-search__clear {
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
  bottom: 10px;
}

.benefits-search__clear-highlight {
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
  font-size: xx-small;
}

/* Search input: a bit more modal-like (dark, subtle border) */
.benefits-search__input {
  background-color: #0f0c0a;
  border: 1px solid #241f19;
  color: #d8cab5;
  padding-left: 30px;
}

.benefits-search__input::placeholder {
  color: rgba(216, 202, 181, 0.55);
  padding-left: 12px;
}

.benefits-shell__kicker {
  font-weight: 800;
  color: #f0d37a;
  letter-spacing: 0.6px;
}
.benefits-shell__divider {
  opacity: 0.55;
  margin: 0 8px;
}
.benefits-shell__hint {
  opacity: 0.9;
}
.perk-col {
  width: 320px;
  min-width: 320px;
}
.perk-col__title {
  font-weight: 900;
  font-size: xx-large;
  color: #f0d37a;
  letter-spacing: 0.4px;
}
.empty-cell {
  padding: 16px 12px;
  text-align: center;
  opacity: 0.85;
}

/* =========================
   Donator Zone Media Section
   ========================= */

.donator-zone-media {
  margin-top: 40px;
  background-color: #161310;
  border: 1px solid #0f0c0a;
  padding: 30px;
}

@media (max-width: 992px) {
  .donator-zone-media {
    padding: 15px;
  }
}

.donator-zone-media__header {
  margin-bottom: 24px;
}

.donator-zone-media__header h3 {
  color: #f0d37a;
  font-family: "Cinzel", serif;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
}

.donator-zone-media__header p {
  margin: 0;
  color: #d8cab5;
  opacity: 0.9;
  max-width: 700px;
}

/* Grid layout (matches modal media grid) */
.donator-zone-media__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
}

@media (max-width: 992px) {
  .donator-zone-media__grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
}

/* Media card */
.donator-zone-media__item {
  background-color: #0f0c0a;
  border: 1px solid #241f19;
  padding: 15px;
  text-align: center;
}

.donator-zone-media__item img {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid #241f19;
}

.donator-zone-media__item h5 {
  margin-top: 15px;
  color: #d8cab5;
  font-weight: 800;
  text-align: center;
}


</style>
